import { LogOut, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface TopBarProps {
  className?: string;
}

/**
 * Top application bar: user avatar/name + sign-out action.
 * Marked as <header> (banner landmark) per WCAG 2.1 AA.
 */
export function TopBar({ className }: TopBarProps) {
  const { profile, signOut } = useAuth();

  const displayName = profile?.full_name ?? profile?.email ?? 'User';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header
      role="banner"
      className={cn(
        'flex h-14 items-center justify-end gap-4 border-b border-border bg-background px-6',
        className,
      )}
    >
      {/* User identity */}
      <div className="flex items-center gap-2 text-sm text-foreground">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium"
          aria-hidden="true"
        >
          {initials}
        </span>
        <span className="hidden sm:block">{displayName}</span>
        <User className="h-4 w-4 text-muted-foreground sm:hidden" aria-hidden="true" />
      </div>

      {/* Sign out */}
      <button
        type="button"
        onClick={() => void signOut()}
        aria-label="Sign out"
        className={cn(
          'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'transition-colors',
        )}
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">Sign out</span>
      </button>
    </header>
  );
}
